import type { Event } from '@sentry/angular';

/** The shape Sentry produces when it serializes a thrown HttpErrorResponse. */
interface SerializedHttpErrorResponse {
  name?: unknown;
  status?: unknown;
}

/**
 * Drops HTTP failures that reach Sentry through window.onerror or
 * unhandledrejection instead of being reported deliberately.
 *
 * The interceptors are the only intended reporters of API errors: they capture
 * 403 and 500 with the status and URL in the message, and stay quiet for every
 * other status. When an HttpErrorResponse also escapes unhandled it never
 * passes through NgZone, so globalHandlersIntegration picks up the raw object
 * and sends its own event - titled "Object captured as exception with keys:
 * error, headers, message, name, ok, status, statusText, url". That is either a
 * duplicate of what an interceptor already reported, or noise for a status we
 * deliberately chose not to report.
 *
 * Sentry serializes the thrown object into `extra.__serialized__`, the only
 * place an HttpErrorResponse stays recognisable on the event.
 */
export const ignoreHttpErrorsIntegration = () => ({
  name: 'IgnoreHttpErrors',

  processEvent(event: Event): Event | null {
    const serialized = event.extra?.['__serialized__'] as
      | SerializedHttpErrorResponse
      | undefined;

    return serialized?.name === 'HttpErrorResponse' ? null : event;
  },
});
